export type EntityLinkType = "system" | "model" | "artifact" | "job";

export interface EntityLink {
  type: EntityLinkType;
  path: string;
}

// Whether the linked object was confirmed to exist. "missing" covers 404, 403 and an id the
// backend left out of a lookup; "unverified" means the check itself failed, not the object.
export type EntityLinkVerdict = "pending" | "found" | "missing" | "unverified";

/**
 * Instructions appended to the chat system prompt so the assistant emits
 * entity references as markdown links matching `parseEntityLink` patterns.
 */
export const ENTITY_LINK_INSTRUCTIONS = `# Entity Links
When you mention a specific Istari object the user can open, format the mention as a markdown link with the object's human-readable name as the link text and one of these relative URLs as the target:
- System: [System Name](/systems/<system_id>)
- Model / resource: [file-name.ext](/resources/<resource_id>)
- Artifact: [artifact-name.ext](/resources/artifact/<artifact_id>)
- Job: [Job Name](/jobs/<job_id>)
For models and resources, the first path segment after /resources/ must be the resource_id — never the file_id or file_revision_id; links whose first segment is any other id do not resolve. To point at a specific revision, append its id as an optional second segment: [file-name.ext](/resources/<resource_id>/<file_revision_id>). Likewise, when the user asks for a resource's identifier, give the resource_id (mention other ids only if they ask for them specifically).
Use the object's full name, exactly as a tool result gives it, as the link text, never a shortened one: the app reads the file type from it. If you must shorten a long name in plain prose, keep its beginning and its file extension and drop the middle (stress-model-artifact-02069….stl, never …02069-4f8a.stl): the start is what the user recognizes.
Only link objects whose IDs appear in tool results or in the context above. Never guess or fabricate an ID. Do not show raw IDs as link text. Objects without a listed URL pattern (for example agents) stay plain text.`;

// RFC 4122 shape — keep in sync with src/shared/utils/is-uuid.ts.
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}";

const ENTITY_PATH_PATTERNS: ReadonlyArray<{ type: EntityLinkType; pattern: RegExp }> = [
  { type: "system", pattern: new RegExp(`^/systems-dashboard/${UUID}$`) },
  { type: "system", pattern: new RegExp(`^/systems/${UUID}$`) },
  { type: "artifact", pattern: new RegExp(`^/artifact/${UUID}(/${UUID})?$`) },
  { type: "artifact", pattern: new RegExp(`^/files/artifact/${UUID}(/${UUID})?$`) },
  { type: "artifact", pattern: new RegExp(`^/resources/artifact/${UUID}(/${UUID})?$`) },
  { type: "model", pattern: new RegExp(`^/files/${UUID}(/${UUID})?$`) },
  { type: "model", pattern: new RegExp(`^/resources/${UUID}(/${UUID})?$`) },
  { type: "job", pattern: new RegExp(`^/jobs/${UUID}$`) },
];

export function parseEntityLink(href: string): EntityLink | null {
  const path = toInternalPath(href);
  if (path === null) return null;
  const match = ENTITY_PATH_PATTERNS.find(({ pattern }) => pattern.test(path));
  return match ? { type: match.type, path } : null;
}

// The primary entity id is always the first UUID segment (revision ids come after it).
export function entityIdFromPath(path: string): string | null {
  return new RegExp(UUID).exec(path)?.[0] ?? null;
}

function toInternalPath(href: string): string | null {
  if (href.startsWith("/")) return href.replace(/[?#].*$/, "");
  try {
    const url = new URL(href);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch {
    return null;
  }
}
